import { getFormProps, getInputProps, useForm } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { json, type ActionFunctionArgs, type LoaderFunctionArgs, type MetaFunction } from '@remix-run/node'
import { Form, Link, useActionData, useSearchParams } from '@remix-run/react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { GeneralErrorBoundary } from '#app/components/error-boundary.tsx'
import { Flex } from '#app/components/layout/flex.tsx'
import { VisuallyHidden } from '#app/components/layout/visually-hidden.tsx'
import { H1 } from '#app/components/typography/h1.tsx'
import { P } from '#app/components/typography/p.tsx'
import { StatusButton } from '#app/components/ui/status-button.tsx'
import { Input } from '#app/old_components/ui/input.tsx'
import { Label } from '#app/old_components/ui/label.tsx'
import { login, requireAnonymous } from '#app/utils/auth.server.ts'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'
import { useIsPending } from '#app/utils/misc.tsx'
import { EmailSchema, PasswordSchema } from '#app/utils/user-validation.ts'
import { handleNewSession } from './login.server.ts'

const LoginFormSchema = z.object({
	email: EmailSchema,
	password: PasswordSchema,
	redirectTo: z.string().optional(),
})

export async function loader({ request }: LoaderFunctionArgs) {
	await requireAnonymous(request)
	return json({})
}

export async function action({ request }: ActionFunctionArgs) {
	await requireAnonymous(request)
	const formData = await request.formData()
	checkHoneypot(formData)
	const submission = parseWithZod(formData, { schema: LoginFormSchema })
	if (submission.status !== 'success') {
		return json(submission.reply(), { status: submission.status === 'error' ? 400 : 200 })
	}
	const { email, password } = submission.value
	const session = await login({ email, password })
	if (session === null) {
		return json(submission.reply({ formErrors: ['Invalid email or password'] }))
	}

	const { redirectTo } = submission.value
	return handleNewSession({ request, session, redirectTo })
}

export default function LoginPage() {
	const lastResult = useActionData<typeof action>()
	const isPending = useIsPending()
	const [searchParams] = useSearchParams()
	const redirectTo = searchParams.get('redirectTo')

	const [form, { email, password }] = useForm({
		id: 'login-form',
		constraint: getZodConstraint(LoginFormSchema),
		defaultValue: { redirectTo },
		lastResult,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: LoginFormSchema })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<Form method="POST" {...getFormProps(form)}>
			<Flex justify="center" items="center">
				<Flex orientation="vertical" gap="10">
					<H1 className="text-center">Welcome back!</H1>
					<HoneypotInputs />
					<VisuallyHidden>
						<Label htmlFor={email.id}>Email</Label>
					</VisuallyHidden>
					<Input
						aria-invalid={email.errorId ? true : undefined}
						aria-describedby={email.errorId}
						autoComplete="email"
						placeholder="Enter your email"
						{...getInputProps(email, { type: 'email' })}
					/>
					{email.errors?.map(e => <P key={e}>{e}</P>)}

					<VisuallyHidden>
						<Label htmlFor={password.id}>Password</Label>
					</VisuallyHidden>
					<Input
						aria-invalid={password.errorId ? true : undefined}
						aria-describedby={password.errorId}
						autoComplete="current-password"
						placeholder="Enter your password"
						{...getInputProps(password, { type: 'password' })}
					/>
					{password.errors?.map(e => <P key={e}>{e}</P>)}
					{form.errors?.map(e => <P key={e}>{e}</P>)}
					<div className="flex items-center justify-between gap-6 pt-3">
						<StatusButton className="w-full" status={isPending ? 'pending' : lastResult?.status ?? 'idle'} type="submit" disabled={isPending}>
							{isPending ? 'Logging in' : 'Log in'}
						</StatusButton>
					</div>
					<Flex justify="between">
						<Link to="/forgot-password">Forgot password?</Link>
						<Flex gap="6">
							<span className="text-muted-foreground">New here?</span>
							<Link to={`/create-account${redirectTo ? `?redirectTo=${redirectTo}` : ''}`}>Create an account</Link>
						</Flex>
					</Flex>
				</Flex>
			</Flex>
		</Form>
	)
}

export const meta: MetaFunction = () => {
	return [{ title: 'Login to Epic Notes' }]
}

export function ErrorBoundary() {
	return <GeneralErrorBoundary />
}
